"use client";

/**
 * The composer's "+" add menu: attach a file, mention, the connectors sub-view
 * with the keyboard-hint footer. Its state lives in
 * `useAddMenu` (ChatInputActions.hooks.ts) so it outlives this component.
 */

import type { TFunction } from "i18next";
import type { UseIntegrationOAuthReturn } from "@/hooks/useIntegrationOAuth";
import type { AddMenuState } from "./ChatInputActions.hooks";

import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { SERVICES } from "@/config/integrations";

type ConnectorStatusSource = Pick<
  UseIntegrationOAuthReturn,
  "connectingToolId" | "getToolConnectionStatus"
>;

// ── Main view ──────────────────────────────────────────────────────────

/**
 * The root list. A plain render function rather than a component, so that it
 * and the connectors view are one host-element tree to React: switching views
 * reconciles their elements by position, exactly as the composer always has.
 */
const renderMainView = (t: TFunction, menu: AddMenuState) => (
  <div className="flex flex-col">
    {/* Header (title + close X), flush inside the popover so the
          border wraps the whole menu top and bottom — mirrors the
          command menu. items-stretch makes the X fill the header
          height and the rounded top-right corner. */}
    <div className="flex items-stretch border-b border-divider">
      <div className="flex flex-1 min-w-0 items-center px-3 py-2">
        <span className="text-xs font-semibold text-default-600">
          {t("chatInput.addMenu.label")}
        </span>
      </div>
      <button
        aria-label={t("Close")}
        className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-main-close"
        type="button"
        onClick={() => menu.onOpenChange(false)}
      >
        <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
      </button>
    </div>

    {/* Body — padding keeps the rounded hover pills off the edges.
          Items use an inset ring (not an offset outline) so the focus
          state is never clipped by the popover's overflow-hidden. */}
    <div className="flex flex-col gap-0.5 p-1">
      {/* Attach file */}
      <button
        data-add-menu-nav
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-attach-file"
        type="button"
        onClick={menu.onAttachFile}
      >
        <Icon className="text-default-500 shrink-0" icon="solar:paperclip-linear" width={16} />
        <span>{t("chatInput.addMenu.attachFile")}</span>
      </button>

      {/* Mention people or workflows */}
      <button
        data-add-menu-nav
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-mention"
        type="button"
        onClick={menu.onMention}
      >
        <span
          aria-hidden="true"
          className="flex w-4 shrink-0 items-center justify-center text-base font-semibold leading-none text-default-500"
        >
          @
        </span>
        <span>{t("chatInput.addMenu.mention")}</span>
      </button>

      {/* Connectors — opens the connectors sub-view */}
      <button
        data-add-menu-nav
        aria-haspopup="menu"
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-connectors"
        type="button"
        onClick={menu.onOpenConnectors}
      >
        <Icon className="text-default-500 shrink-0" icon="solar:plug-circle-linear" width={16} />
        <span className="flex-1">{t("chatInput.addMenu.connectors")}</span>
        <Icon
          className="text-default-400 shrink-0"
          icon="solar:alt-arrow-right-linear"
          width={16}
        />
      </button>
    </div>
  </div>
);

// ── Connectors sub-view ────────────────────────────────────────────────

interface ConnectorStateProps {
  serviceId: string;
  isConnected: boolean;
  isConnecting: boolean;
}

/** A connector's trailing state: a spinner while connecting, then Connected or Connect. */
const ConnectorState = ({ serviceId, isConnected, isConnecting }: ConnectorStateProps) => {
  const { t } = useTranslation();

  return isConnecting ? (
    <Spinner
      aria-label={t("Connecting...")}
      data-id={`add-menu-connector-${serviceId}-spinner`}
      size="sm"
    />
  ) : isConnected ? (
    <span className="text-tiny text-default-400 shrink-0">{t("Connected")}</span>
  ) : (
    <span className="text-tiny text-primary-600 shrink-0">{t("Connect")}</span>
  );
};

interface ConnectorRowProps {
  service: (typeof SERVICES)[number];
  integrations: ConnectorStatusSource;
  onConnectorPress: (serviceId: string) => void;
}

/** One connector, driven by the real OAuth services. */
const ConnectorRow = ({ service, integrations, onConnectorPress }: ConnectorRowProps) => {
  const { t } = useTranslation();
  const status = integrations.getToolConnectionStatus(service.id);
  const isConnected = status?.connected ?? false;
  const isConnecting = integrations.connectingToolId === service.id;

  return (
    <button
      data-add-menu-nav
      className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset disabled:cursor-default disabled:hover:bg-transparent"
      data-id={`add-menu-connector-${service.id}`}
      disabled={isConnected || isConnecting}
      type="button"
      onClick={() => onConnectorPress(service.id)}
    >
      <Icon className="shrink-0" icon={service.icon} width={18} />
      <span className="flex-1 truncate">{t(service.name)}</span>
      <ConnectorState
        isConnected={isConnected}
        isConnecting={isConnecting}
        serviceId={service.id}
      />
    </button>
  );
};

/** The connectors sub-view, rendered as a plain function for the reason given on `renderMainView`. */
const renderConnectorsView = (
  t: TFunction,
  menu: AddMenuState,
  integrations: ConnectorStatusSource,
) => (
  <div className="flex flex-col">
    {/* Connectors sub-view header: back navigation + close (X),
          flush inside the popover border to match the main view and
          the command menu. */}
    <div className="flex items-stretch border-b border-divider">
      <button
        className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-connectors-back"
        type="button"
        onClick={menu.onShowMain}
      >
        <Icon className="w-4 h-4 text-default-500 shrink-0" icon="solar:alt-arrow-left-linear" />
        <span className="text-xs font-semibold text-default-600 truncate">
          {t("chatInput.addMenu.connectors")}
        </span>
      </button>
      <button
        aria-label={t("Close")}
        className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-connectors-close"
        type="button"
        onClick={() => menu.onOpenChange(false)}
      >
        <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
      </button>
    </div>

    {/* Body */}
    <div className="flex flex-col gap-0.5 p-1">
      {/* Connector list — driven by the real OAuth services */}
      {SERVICES.map((service) => (
        <ConnectorRow
          key={service.id}
          integrations={integrations}
          service={service}
          onConnectorPress={menu.onConnectorPress}
        />
      ))}

      <div className="my-1 h-px bg-default-200" />

      {/* Manage connectors — full settings page */}
      <button
        data-add-menu-nav
        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
        data-id="add-menu-manage-connectors"
        type="button"
        onClick={menu.onManageConnectors}
      >
        <Icon className="text-default-500 shrink-0" icon="solar:settings-linear" width={16} />
        <span>{t("chatInput.addMenu.manageConnectors")}</span>
      </button>
    </div>
  </div>
);

// ── Footer ─────────────────────────────────────────────────────────────

interface AddMenuFooterProps {
  onOpenChatInputSettings: () => void;
}

/** Footer — keyboard hints + settings cog, matching the command menu. */
const AddMenuFooter = ({ onOpenChatInputSettings }: AddMenuFooterProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-2 border-t border-divider px-3 py-2 text-xs text-default-400">
      <div className="flex shrink-0 items-center gap-3">
        <span className="flex items-center gap-1">
          <kbd className="rounded bg-default-100 px-1 py-0.5 font-mono text-[10px]">↑↓</kbd>
          {t("chatCommandMenu.footer.navigate")}
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded bg-default-100 px-1 py-0.5 font-mono text-[10px]">Enter</kbd>
          {t("chatCommandMenu.footer.select")}
        </span>
        <span className="flex items-center gap-1">
          <kbd className="rounded bg-default-100 px-1 py-0.5 font-mono text-[10px]">Esc</kbd>
          {t("chatCommandMenu.footer.close")}
        </span>
      </div>
      <Tooltip>
        <TooltipTrigger>
          <button
            aria-label={t("chatCommandMenu.footer.manageInSettings")}
            className="flex shrink-0 items-center rounded text-primary hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
            type="button"
            onClick={onOpenChatInputSettings}
          >
            <Icon className="w-4 h-4" icon="solar:settings-linear" />
          </button>
        </TooltipTrigger>
        <TooltipContent showArrow>{t("chatCommandMenu.footer.manageInSettings")}</TooltipContent>
      </Tooltip>
    </div>
  );
};

// ── Menu ───────────────────────────────────────────────────────────────

interface AddMenuProps {
  /** Shown only when file upload is permitted. */
  show: boolean;
  menu: AddMenuState;
  integrations: ConnectorStatusSource;
}

/** "+" Add menu — shown when file upload is permitted. */
export const AddMenu = ({ show, menu, integrations }: AddMenuProps) => {
  const { t } = useTranslation();

  if (!show) return null;

  return (
    <Popover isOpen={menu.isOpen} placement="top-start" onOpenChange={menu.onOpenChange}>
      <PopoverTrigger>
        <Button
          isIconOnly
          aria-label={t("chatInput.addMenu.label")}
          className="rounded-full border border-default-300 bg-default-100 hover:bg-default-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          data-id="add-menu-button"
          size="sm"
          variant="ghost"
        >
          <Icon icon="solar:add-circle-linear" width={18} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-60 overflow-hidden rounded-xl border border-divider outline-none">
        {menu.view === "main"
          ? renderMainView(t, menu)
          : renderConnectorsView(t, menu, integrations)}

        <AddMenuFooter onOpenChatInputSettings={menu.onOpenChatInputSettings} />
      </PopoverContent>
    </Popover>
  );
};
